import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { useWeightLog } from '../hooks/useWeightLog';
import { getWeekStart } from '../lib/utils';
import { C } from '../lib/theme';
import { getProspectoUrl } from '../lib/prospecto';

const MEAL_ORDER = ['desayuno', 'comida', 'cena', 'postre', 'guarrada'];

function getMealLabels(t) {
  return {
    desayuno: { label: t('documents.meal_breakfast'), icon: '☀️' },
    comida:   { label: t('documents.meal_lunch'),     icon: '🍽️' },
    cena:     { label: t('documents.meal_dinner'),    icon: '🌙' },
    postre:   { label: t('documents.meal_dessert'),   icon: '🍮' },
    guarrada: { label: t('documents.meal_treat'),     icon: '⚡' },
  };
}

function formatDateShort(dateStr, dayNames, monthNames) {
  const d = new Date(dateStr + 'T00:00:00');
  return `${dayNames[d.getDay()]} ${d.getDate()} ${monthNames[d.getMonth()]}`;
}

function buildWeekLabel(monthNames) {
  const weekStart = getWeekStart(new Date());
  const s = new Date(weekStart + 'T00:00:00');
  const e = new Date(s);
  e.setDate(s.getDate() + 6);
  return `${s.getDate()} ${monthNames[s.getMonth()]} — ${e.getDate()} ${monthNames[e.getMonth()]}`;
}

function generateSummary(logs, profile, t) {
  const weekStart = getWeekStart(new Date());
  const weekLogs = logs.filter(l => l.logged_at >= weekStart);

  const firstWeight      = logs[0]?.weight;
  const lastWeight       = logs[logs.length - 1]?.weight;
  const weekFirstW       = weekLogs[0]?.weight;
  const weekLastW        = weekLogs[weekLogs.length - 1]?.weight;
  const totalLost        = firstWeight && lastWeight ? +(firstWeight - lastWeight).toFixed(1) : null;
  const weekChange       = weekFirstW && weekLastW && weekLogs.length > 1
    ? +(weekFirstW - weekLastW).toFixed(1) : null;
  const weeksOnTreatment = profile?.consent_at
    ? Math.floor((Date.now() - new Date(profile.consent_at)) / (1000 * 60 * 60 * 24 * 7))
    : 0;

  const parts = [];

  if (!weekLogs.length) {
    parts.push(t('documents.summary_no_logs'));
  } else if (weekChange === null) {
    parts.push(t('documents.summary_single', { weight: weekLastW || weekFirstW }));
  } else if (weekChange > 0) {
    parts.push(t('documents.summary_down', { change: weekChange }));
  } else if (weekChange < 0) {
    parts.push(t('documents.summary_up', { change: Math.abs(weekChange) }));
  } else {
    parts.push(t('documents.summary_stable'));
  }

  if (totalLost !== null && totalLost > 0) {
    parts.push(t('documents.summary_total', { total: totalLost }));
  }

  if (weeksOnTreatment > 0) {
    parts.push(t('documents.summary_weeks', { weeks: weeksOnTreatment }));
  }

  return parts.join(' ');
}

// ─── WEEKLY MENU SECTION ─────────────────────────────────────────────────────

function WeeklyMenuSection({ plan, loading, weekLabel }) {
  const { t } = useTranslation();
  const dayNames   = t('common.days_short_sun', { returnObjects: true });
  const monthNames = t('common.months_short',   { returnObjects: true });
  const MEAL_LABELS = getMealLabels(t);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: C.gray, fontSize: '0.9rem' }}>
        {t('planner.loading')}
      </div>
    );
  }

  if (!plan) {
    return (
      <div style={{ background: '#fff', borderRadius: 16, padding: '1.5rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📅</div>
        <div style={{ color: C.carbon, fontFamily: 'Georgia, serif', marginBottom: '0.4rem' }}>{t('documents.menu_empty_title')}</div>
        <div style={{ color: C.gray, fontSize: '0.85rem' }}>{t('documents.menu_empty_sub')}</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: '0.72rem', color: C.gray, marginBottom: '0.8rem', textAlign: 'center' }}>
        {t('documents.week_label', { label: weekLabel, days: plan.days?.length || 0 })}
      </div>
      {(plan.days || []).map(day => {
        let dayCalories = 250, dayProtein = 0;
        for (const slot of Object.values(day.meals || {})) {
          const items = Array.isArray(slot) ? slot : [slot];
          for (const r of items) {
            dayCalories += r?.calories_per_serving || 0;
            dayProtein  += r?.protein_per_serving  || 0;
          }
        }
        return (
          <div key={day.date} style={{ background: '#fff', borderRadius: 14, marginBottom: '0.6rem', overflow: 'hidden', boxShadow: '0 1px 8px rgba(44,106,79,0.07)' }}>
            <div style={{ padding: '0.6rem 0.9rem', background: C.mintLt, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, color: C.sage, fontSize: '0.88rem' }}>
                {formatDateShort(day.date, dayNames, monthNames)}
                {day.injection_day_itself && <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', color: '#1D4ED8' }}>💉</span>}
                {day.injection_window && !day.injection_day_itself && <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', color: '#1D4ED8' }}>💉+24h</span>}
                {day.dessert_day && <span style={{ marginLeft: '0.35rem', fontSize: '0.7rem' }}>🍮</span>}
                {day.guarrada_day && <span style={{ marginLeft: '0.35rem', fontSize: '0.7rem' }}>⚡</span>}
                {day.fasting && <span style={{ marginLeft: '0.35rem', fontSize: '0.7rem', color: '#92400E' }}>🕐</span>}
              </span>
              <span style={{ fontSize: '0.72rem', color: C.carbon }}>
                {Math.round(dayCalories)} kcal · {Math.round(dayProtein)}g
              </span>
            </div>
            <div style={{ padding: '0.5rem 0.9rem 0.6rem' }}>
              {MEAL_ORDER.map(type => {
                const slot = day.meals?.[type];
                if (!slot) return null;
                const items = Array.isArray(slot) ? slot : [slot];
                const meta = MEAL_LABELS[type];
                return (
                  <div key={type} style={{ marginBottom: '0.25rem', lineHeight: 1.4 }}>
                    <span style={{ fontSize: '0.72rem', color: C.gray, fontWeight: 600 }}>{meta.icon} {meta.label}: </span>
                    <span style={{ fontSize: '0.78rem', color: C.carbon }}>
                      {items.map(r => r?.name).filter(Boolean).join(' + ')}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── DOCTOR NOTE SECTION ─────────────────────────────────────────────────────

function DoctorNoteSection({ logs, profile, weekLabel }) {
  const { t } = useTranslation();
  const [checked, setChecked] = useState(new Set());

  const DOCTOR_TOPICS = [
    { id: 'nauseas',      label: t('documents.topic_nausea')   },
    { id: 'saciedad',     label: t('documents.topic_satiety')  },
    { id: 'dosis',        label: t('documents.topic_dose')     },
    { id: 'conservacion', label: t('documents.topic_storage')  },
    { id: 'social',       label: t('documents.topic_social')   },
    { id: 'energia',      label: t('documents.topic_energy')   },
    { id: 'plan',         label: t('documents.topic_plan')     },
    { id: 'antojos',      label: t('documents.topic_cravings') },
  ];

  const lastWeight  = logs[logs.length - 1]?.weight;
  const firstWeight = logs[0]?.weight;
  const totalLost   = firstWeight && lastWeight ? +(firstWeight - lastWeight).toFixed(1) : null;
  const summary     = generateSummary(logs, profile, t);

  function toggle(id) {
    setChecked(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  return (
    <div>
      <div style={{ background: `linear-gradient(135deg, ${C.sage}, ${C.sageLt})`, borderRadius: 16, padding: '1.2rem', color: '#fff', marginBottom: '1rem' }}>
        <div style={{ fontFamily: 'Georgia, serif', fontSize: '1rem', fontWeight: 700, marginBottom: '0.2rem' }}>
          {t('documents.doctor_header')}
        </div>
        <div style={{ fontSize: '0.8rem', opacity: 0.85 }}>
          {t('documents.doctor_week', { label: weekLabel, name: profile?.name || '' })}
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, padding: '1.2rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: C.sage, marginBottom: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {t('documents.weight_section')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '0.8rem' }}>
          {[
            { label: t('documents.last_weight'),    value: lastWeight  ? `${lastWeight} kg`  : '—' },
            { label: t('documents.total_lost'),     value: totalLost !== null ? (totalLost >= 0 ? `-${totalLost} kg` : `+${Math.abs(totalLost)} kg`) : '—' },
            { label: t('documents.start_weight'),   value: firstWeight ? `${firstWeight} kg` : '—' },
            { label: t('documents.total_records'),  value: logs.length || '—' },
          ].map(s => (
            <div key={s.label} style={{ background: C.cream, borderRadius: 10, padding: '0.6rem' }}>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '1.05rem', fontWeight: 700, color: C.carbon }}>{s.value}</div>
              <div style={{ fontSize: '0.63rem', color: C.gray, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
        {summary && (
          <div style={{ background: C.mintLt, borderRadius: 10, padding: '0.7rem 0.9rem', fontSize: '0.82rem', color: C.carbon, lineHeight: 1.55, borderLeft: `3px solid ${C.sage}` }}>
            <span style={{ fontWeight: 700, color: C.sage }}>{t('documents.vera_prefix')}</span>{summary}
          </div>
        )}
      </div>

      <div style={{ background: '#fff', borderRadius: 16, padding: '1.2rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: C.sage, marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {t('documents.checklist_title')}
        </div>
        <div style={{ fontSize: '0.75rem', color: C.gray, marginBottom: '0.8rem' }}>
          {t('documents.checklist_sub')}
        </div>
        {DOCTOR_TOPICS.map((topic, idx) => {
          const done = checked.has(topic.id);
          return (
            <div
              key={topic.id}
              onClick={() => toggle(topic.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.65rem',
                padding: '0.65rem 0', cursor: 'pointer', userSelect: 'none',
                borderBottom: idx < DOCTOR_TOPICS.length - 1 ? `1px solid ${C.mintLt}` : 'none',
              }}
            >
              <div style={{
                width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                border: `2px solid ${done ? C.sage : C.mint}`,
                background: done ? C.sage : '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {done && <span style={{ color: '#fff', fontSize: '0.65rem', fontWeight: 900 }}>✓</span>}
              </div>
              <span style={{ fontSize: '0.83rem', color: C.carbon, lineHeight: 1.4 }}>{topic.label}</span>
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: '0.72rem', color: C.gray, textAlign: 'center', paddingBottom: '1rem', lineHeight: 1.5 }}>
        {t('documents.disclaimer')}
      </div>
    </div>
  );
}

// ─── PROSPECTO SECTION ───────────────────────────────────────────────────────

function ProspectoSection({ profile }) {
  const { t, i18n } = useTranslation();
  const url = getProspectoUrl(profile?.drug, i18n.language);

  if (!url) {
    return (
      <div style={{ background: '#fff', borderRadius: 16, padding: '1.5rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📄</div>
        <div style={{ color: C.carbon, fontFamily: 'Georgia, serif', marginBottom: '0.4rem' }}>{t('documents.prospecto_empty_title')}</div>
        <div style={{ color: C.gray, fontSize: '0.85rem' }}>{t('documents.prospecto_empty_sub')}</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', marginBottom: '0.8rem' }}>
        <iframe
          src={url}
          title="Prospecto"
          style={{ width: '100%', height: '70vh', border: 'none', display: 'block' }}
        />
      </div>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: 'block', textAlign: 'center', color: C.sage, fontSize: '0.85rem', fontWeight: 700, textDecoration: 'none' }}
      >
        {t('documents.prospecto_open_new_tab')} ↗
      </a>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────

export default function DocumentsTab({ userId, profile }) {
  const { t } = useTranslation();
  const { logs } = useWeightLog(userId);
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [section, setSection] = useState('menu');

  const monthNames = t('common.months_short', { returnObjects: true });
  const weekLabel  = buildWeekLabel(monthNames);

  useEffect(() => {
    if (!userId) return;
    const weekStart = getWeekStart(new Date());
    supabase
      .from('meal_plans')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.week_start === weekStart) setPlan(data.plan);
        setPlanLoading(false);
      });
  }, [userId]);

  const SECTIONS = [
    { id: 'menu',      label: t('documents.tab_menu') },
    { id: 'doctor',    label: t('documents.tab_doctor') },
    { id: 'prospecto', label: t('documents.tab_prospecto') },
  ];

  return (
    <div>
      <div style={{ display: 'flex', background: '#fff', borderRadius: 12, padding: '0.3rem', marginBottom: '1rem', boxShadow: '0 1px 6px rgba(44,106,79,0.07)' }}>
        {SECTIONS.map(s => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            style={{
              flex: 1, padding: '0.5rem', borderRadius: 9, border: 'none', cursor: 'pointer',
              background: section === s.id ? C.sage : 'transparent',
              color: section === s.id ? '#fff' : C.gray,
              fontWeight: section === s.id ? 700 : 400,
              fontSize: '0.82rem', transition: 'all 0.15s',
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === 'menu' && (
        <WeeklyMenuSection plan={plan} loading={planLoading} weekLabel={weekLabel} />
      )}
      {section === 'doctor' && (
        <DoctorNoteSection logs={logs} profile={profile} weekLabel={weekLabel} />
      )}
      {section === 'prospecto' && (
        <ProspectoSection profile={profile} />
      )}
    </div>
  );
}
